/**
 * Export par lot depuis un CSV (« Importer une base CSV », EditorView) : une
 * étiquette par ligne, chaque `{{colonne}}` du modèle remplacée par la valeur
 * de la ligne.
 *
 * Avant le 2026-10-09, le remplacement ne touchait que `el.text`. Or un calque
 * texte retouché au double-clic porte AUSSI son texte en rich text (`content`),
 * et c'est ce rich text que le PDF imprime (build_html.py) — comme l'écran. Les
 * sept gabarits de l'atelier sont tous dans ce cas : leurs PDF de lot sortaient
 * avec « {{nom}} » écrit en toutes lettres.
 */
import { refusCodeBarres } from './codeBarres';

/**
 * Le texte d'un fichier CSV, dans l'encodage où Excel l'a écrit.
 *
 * Excel en français enregistre « CSV (séparateur : point-virgule) » en
 * Windows-1252. Lu en UTF-8 (`readAsText`), « Crème brûlée » sortait
 * « Cr�me br�l�e » dans chaque PDF du lot (relevé par Jabir sur #306). On essaie
 * l'UTF-8 STRICT, qui refuse le moindre octet invalide ; s'il refuse, c'est du
 * Windows-1252. Le pari est sûr dans ce sens : une lettre accentuée en
 * Windows-1252 n'est jamais de l'UTF-8 valide. La marque d'ordre (BOM) d'un
 * « CSV UTF-8 » d'Excel est retirée au passage.
 *
 * @param {ArrayBuffer|Uint8Array} octets  le fichier, lu par readAsArrayBuffer
 */
export function decoderCsv(octets) {
    const donnees = octets instanceof Uint8Array ? octets : new Uint8Array(octets);
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(donnees);
    } catch {
        return new TextDecoder('windows-1252').decode(donnees)
            .replace(/[\u0080-\u009F]/g, (c) => String.fromCharCode(C1_WINDOWS_1252[c.charCodeAt(0) - 0x80]));
    }
}

/**
 * Les octets 0x80 à 0x9F en Windows-1252, dans l'ordre (norme WHATWG Encoding).
 * Certains moteurs les décodent comme du Latin-1, en caractères de contrôle :
 * Node 22 et Node 24 donnent U+0080 pour « € », U+0092 pour « ’ », U+009C pour
 * « œ » (relevé par Jabir sur #311, reproduit sous Node 24.11). Chrome et Node 26
 * décodent juste ; la table les remet à leur place partout. Les cinq octets que
 * Windows-1252 ne définit pas (0x81, 0x8D, 0x8F, 0x90, 0x9D) restent tels quels,
 * comme le veut la norme.
 */
const C1_WINDOWS_1252 = [
    0x20AC, 0x0081, 0x201A, 0x0192, 0x201E, 0x2026, 0x2020, 0x2021,
    0x02C6, 0x2030, 0x0160, 0x2039, 0x0152, 0x008D, 0x017D, 0x008F,
    0x0090, 0x2018, 0x2019, 0x201C, 0x201D, 0x2022, 0x2013, 0x2014,
    0x02DC, 0x2122, 0x0161, 0x203A, 0x0153, 0x009D, 0x017E, 0x0178,
];

/**
 * Les lignes d'un CSV : { entetes, lignes: [{ colonne: valeur }] }, ou
 * { entetes: null } pour un fichier vide. Séparateur « ; » s'il apparaît dans
 * l'en-tête, sinon « , ». Pas de guillemets : une valeur ne contient pas le
 * séparateur.
 */
export function lireCsv(texte) {
    const brutes = String(texte || '').split('\n').map((l) => l.trim()).filter((l) => l);
    if (brutes.length === 0) return { entetes: null, lignes: [] };
    const separateur = brutes[0].includes(';') ? ';' : ',';
    const entetes = brutes[0].split(separateur).map((h) => h.trim());
    const lignes = brutes.slice(1).map((brute) => {
        const valeurs = brute.split(separateur).map((v) => v.trim());
        const ligne = {};
        entetes.forEach((h, i) => { ligne[h] = valeurs[i] || ''; });
        return ligne;
    });
    return { entetes, lignes };
}

const balise = (colonne) => `{{${colonne}}}`;

/** Un texte simple, ses `{{colonne}}` remplacées. Sans expression régulière :
 *  une colonne « Prix (€) » ou « lot.n° » se remplace telle quelle. */
export function remplirTexte(texte, ligne) {
    if (typeof texte !== 'string') return texte;
    let sortie = texte;
    for (const [colonne, valeur] of Object.entries(ligne)) sortie = sortie.split(balise(colonne)).join(valeur);
    return sortie;
}

/**
 * Les `{{colonne}}` d'un paragraphe de rich text, remplacées en gardant le style
 * de chaque passage. Une balise peut chevaucher deux passages (« {{no » en gras,
 * « m}} » normal, si quelqu'un l'a stylée à moitié) : la valeur prend alors le
 * style du passage où la balise commence, et le reste de la balise disparaît
 * des passages suivants. Un saut de ligne (nœud non texte) coupe une balise.
 */
function remplirParagraphe(noeuds, ligne) {
    // Le texte du paragraphe, et où commence chaque passage dans ce texte.
    const debuts = [];
    let plein = '';
    for (const n of noeuds) {
        debuts.push(plein.length);
        plein += n.type === 'text' ? (n.text || '') : '\u0000';
    }
    // Toutes les balises connues, de la première à la dernière, sans chevauchement.
    const trouvees = [];
    for (const [colonne, valeur] of Object.entries(ligne)) {
        const b = balise(colonne);
        for (let i = plein.indexOf(b); i !== -1; i = plein.indexOf(b, i + b.length)) {
            trouvees.push({ debut: i, fin: i + b.length, valeur });
        }
    }
    trouvees.sort((a, b) => a.debut - b.debut);
    const retenues = trouvees.filter((t, i, toutes) => i === 0 || t.debut >= toutes[i - 1].fin);
    if (retenues.length === 0) return noeuds;

    const sortie = noeuds.map((n) => ({ ...n }));
    const passageA = (pos) => {
        let k = 0;
        while (k + 1 < sortie.length && debuts[k + 1] <= pos) k += 1;
        return k;
    };
    // De la dernière à la première : les positions des précédentes ne bougent pas.
    for (const { debut, fin, valeur } of retenues.reverse()) {
        const a = passageA(debut);
        const b = passageA(fin - 1);
        const ta = sortie[a].text;
        if (a === b) {
            sortie[a].text = ta.slice(0, debut - debuts[a]) + valeur + ta.slice(fin - debuts[a]);
        } else {
            sortie[a].text = ta.slice(0, debut - debuts[a]) + valeur;
            for (let k = a + 1; k < b; k += 1) sortie[k].text = '';
            sortie[b].text = sortie[b].text.slice(fin - debuts[b]);
        }
    }
    // Tiptap refuse un passage de texte vide.
    return sortie.filter((n) => n.type !== 'text' || n.text);
}

/** Un rich text (JSON Tiptap), ses `{{colonne}}` remplacées. */
export function remplirContenu(content, ligne) {
    if (!content || !Array.isArray(content.content)) return content;
    return {
        ...content,
        content: content.content.map((p) => (Array.isArray(p.content)
            ? { ...p, content: remplirParagraphe(p.content, ligne) }
            : p)),
    };
}

/** Les calques d'une étiquette du lot : textes (simple ET rich text) et codes-barres remplis. */
export function remplirElements(elements, ligne) {
    return elements.map((el) => {
        if (el.type === 'barcode') return { ...el, text: remplirTexte(el.text, ligne) };
        if (el.type !== 'text') return el;
        const rempli = { ...el, text: remplirTexte(el.text, ligne) };
        if (el.content) rempli.content = remplirContenu(el.content, ligne);
        return rempli;
    });
}

/**
 * Les codes-barres du lot qui ne se codent pas, vérifiés AVANT le premier
 * téléchargement : sinon le lot s'arrêtait en route, une partie des PDF déjà
 * téléchargée. Un calque masqué n'est pas imprimé : il ne bloque rien.
 * Les lignes sont comptées comme dans le fichier (l'en-tête est la ligne 1).
 */
export async function refusDuLot(etiquettes) {
    const refus = [];
    for (let i = 0; i < etiquettes.length; i++) {
        for (const el of etiquettes[i].filter((e) => e.type === 'barcode' && !e.hidden)) {
            const raison = await refusCodeBarres(el);
            if (raison) refus.push(`ligne ${i + 2} (« ${el.text} ») : ${raison}`);
        }
    }
    return refus;
}

/**
 * Les balises `{{…}}` du modèle qu'aucune colonne du CSV ne remplit : elles
 * sortiraient telles quelles dans chaque PDF. Lues là où le PDF les lit — le
 * rich text s'il existe, sinon le texte simple.
 */
export function balisesSansColonne(elements, entetes) {
    const connues = new Set(entetes);
    const orphelines = new Set();
    for (const el of elements) {
        if (el.hidden) continue;          // il ne sortira pas dans le PDF
        let texte = '';
        if (el.type === 'barcode') texte = el.text || '';
        else if (el.type === 'text') {
            texte = el.content && Array.isArray(el.content.content)
                ? el.content.content.map((p) => (p.content || []).map((n) => n.text || '').join('')).join('\n')
                : (el.text || '');
        }
        for (const [, colonne] of texte.matchAll(/\{\{([^{}]+)\}\}/g)) {
            if (!connues.has(colonne)) orphelines.add(colonne);
        }
    }
    return [...orphelines];
}
